/**
 * FrequencyIndicator Component
 * 
 * Displays the current frequency value with a visual frequency scale.
 * Used for auto-wah and other frequency-based effects.
 */

import { useMemo } from 'react';
import './FrequencyIndicator.css';

/**
 * Props for FrequencyIndicator component
 */
export interface FrequencyIndicatorProps {
  /** Current frequency in Hz */
  frequency: number;
  /** Minimum frequency in Hz */
  minFreq?: number;
  /** Maximum frequency in Hz */
  maxFreq?: number;
  /** Width of the indicator */
  width?: number;
  /** Height of the indicator */
  height?: number;
}

/**
 * FrequencyIndicator component
 * 
 * Features:
 * - Displays frequency value with Hz/kHz formatting
 * - Visual frequency scale (logarithmic)
 * - Smooth position updates
 * - Frequency markers
 * 
 * @param props - Component props
 * @returns FrequencyIndicator component
 */
export function FrequencyIndicator({
  frequency,
  minFreq = 20,
  maxFreq = 20000,
  width = 200,
  height = 40,
}: FrequencyIndicatorProps) {
  /**
   * Format frequency for display
   */
  const formattedFreq = useMemo(() => {
    if (frequency >= 1000) {
      const kHz = frequency / 1000;
      return `${kHz.toFixed(2)} kHz`;
    }
    return `${Math.round(frequency)} Hz`;
  }, [frequency]);

  /**
   * Calculate position on logarithmic scale
   */
  const position = useMemo(() => {
    const logMin = Math.log10(minFreq);
    const logMax = Math.log10(maxFreq);
    const logFreq = Math.log10(frequency);
    
    const normalized = (logFreq - logMin) / (logMax - logMin);
    return Math.max(0, Math.min(1, normalized)) * 100;
  }, [frequency, minFreq, maxFreq]);

  /**
   * Frequency markers for the scale
   */
  const markers = useMemo(() => {
    const freqMarkers = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000];
    
    return freqMarkers.map((freq) => {
      const logMin = Math.log10(minFreq);
      const logMax = Math.log10(maxFreq);
      const logFreq = Math.log10(freq);
      
      const pos = ((logFreq - logMin) / (logMax - logMin)) * 100;
      
      if (pos < 0 || pos > 100) return null;
      
      const label = freq >= 1000 ? `${freq / 1000}k` : `${freq}`;
      
      return { freq, pos, label };
    }).filter(Boolean) as Array<{ freq: number; pos: number; label: string }>;
  }, [minFreq, maxFreq]);

  return (
    <div className="frequency-indicator" style={{ width: `${width}px` }}>
      {/* Frequency value display */}
      <div className="frequency-indicator-value">
        {formattedFreq}
      </div>
      
      {/* Frequency scale */}
      <div className="frequency-indicator-scale" style={{ height: `${height}px` }}>
        {/* Scale background */}
        <div className="frequency-indicator-track" />
        
        {/* Frequency markers */}
        {markers.map((marker) => (
          <div
            key={marker.freq}
            className="frequency-indicator-marker"
            style={{ left: `${marker.pos}%` }}
          >
            <div className="frequency-indicator-marker-tick" />
            <div className="frequency-indicator-marker-label">
              {marker.label}
            </div>
          </div>
        ))}
        
        {/* Current frequency indicator */}
        <div
          className="frequency-indicator-pointer"
          style={{ left: `${position}%` }}
        >
          <div className="frequency-indicator-pointer-line" />
          <div className="frequency-indicator-pointer-dot" />
        </div>
      </div>
    </div>
  );
}
